import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { type: "bill", title: "", link: "", summary: "", sections: "", concerns: "", mp_vote: "", status: "watching" };
const STATUSES = ["watching", "passed", "defeated", "withdrawn", "lapsed"];
const VOTES = ["", "for", "against", "abstained", "absent", "unknown"];

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="font-type text-xs uppercase tracking-wider text-[#1B2A41]">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-[#5C6A79]">{hint}</p>}
  </div>
);

export const BillForm = ({ initial, mpName, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "bg-white border-[#D8D2C4] focus-visible:ring-[#E2705A]";
  const select = "h-10 w-full rounded-md border border-[#D8D2C4] bg-white px-3 text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, mp_vote: form.type === "vote" ? form.mp_vote : "" };
      const { data } = initial?.id ? await api.put(`/bills/${initial.id}`, payload) : await api.post("/bills", payload);
      toast.success(initial?.id ? "Entry updated" : "Logged — thanks for bringing the receipts");
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="bill-form" className="space-y-4 rounded-2xl bg-[#FAF8F2] border-2 border-[#E2705A]/40 p-5">
      <div className="flex gap-2">
        {["bill", "vote"].map((t) => (
          <button type="button" key={t} data-testid={`bill-type-${t}`} onClick={() => setForm({ ...form, type: t })} className={`flex-1 h-10 rounded-full border-2 text-sm font-semibold capitalize transition-colors ${form.type === t ? "bg-[#1B2A41] border-[#1B2A41] text-[#FDFBF7]" : "border-[#D8D2C4] hover:border-[#8FA58A]"}`}>
            {t === "bill" ? "A bill before Parliament" : `A vote ${mpName ? `by ${mpName}` : "cast"}`}
          </button>
        ))}
      </div>
      <F label="Title">
        <Input data-testid="bill-title-input" required minLength={3} value={form.title} onChange={set("title")} placeholder="e.g. Digital ID Bill 2024" className={input} />
      </F>
      <F label="Source link" hint="APH bill page, Hansard, or the division record. https:// links only.">
        <Input data-testid="bill-link-input" type="url" value={form.link} onChange={set("link")} placeholder="https://www.aph.gov.au/..." className={input} />
      </F>
      <F label="What it actually is / does">
        <Textarea data-testid="bill-summary-input" required minLength={3} rows={3} value={form.summary} onChange={set("summary")} className={input} />
      </F>
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Section numbers" hint="Instrument beats inference — point at the clause.">
          <Input data-testid="bill-sections-input" value={form.sections} onChange={set("sections")} placeholder="e.g. s 12(3), Sch 2 cl 7" className={input} />
        </F>
        <F label="Status">
          <select data-testid="bill-status-select" value={form.status} onChange={set("status")} className={select}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </F>
      </div>
      {form.type === "vote" && (
        <F label={`How did ${mpName || "the member"} vote?`}>
          <select data-testid="bill-mp-vote-select" value={form.mp_vote} onChange={set("mp_vote")} className={select}>
            {VOTES.map((v) => <option key={v} value={v}>{v || "— select —"}</option>)}
          </select>
        </F>
      )}
      <F label="What's wrong with it (or worth watching)">
        <Textarea data-testid="bill-concerns-input" rows={3} value={form.concerns} onChange={set("concerns")} className={input} />
      </F>
      <div className="flex gap-2 justify-end">
        {onCancel && <button type="button" onClick={onCancel} data-testid="bill-cancel-button" className="h-11 px-5 rounded-full border border-[#D8D2C4] text-sm font-semibold hover:border-[#1B2A41]">Cancel</button>}
        <button type="submit" disabled={busy} data-testid="bill-submit-button" className="h-11 px-6 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {initial?.id ? "Save changes" : "Log it"}
        </button>
      </div>
    </form>
  );
};
